/* Signalen per project voor de projectlijst: wanneer er voor het laatst iets
 * gebeurde, hoeveel taken openstaan, of er een live EMS-locatie is, en hoe de
 * laatste offerte ervoor staat. Eén keer ophalen voor alle projecten samen (een
 * paar kleine queries), dan per project samenvoegen in bouwSignalen().
 *
 * Een tabel die de gebruiker niet mag lezen (project_notes is admin-only) telt
 * gewoon niet mee; dat is geen fout. */

import { isVerlopen, lokaleDag } from '../../offertes/data/status';

export interface SignalenClient {
  from(tabel: string): any;
  schema(naam: string): { from(tabel: string): any };
}

export interface Signalen {
  /** ISO-tijdstip van de laatste notitie, taak, bestandsversie, offerte of mail; null als er niets is. */
  laatsteActiviteit: string | null;
  openTaken: number;
  /** Er hangt een actieve EMS-locatie aan (direct of via de Site). */
  ems: boolean;
  /** Status van de meest recent bijgewerkte offerte in het CRM; 'expired' als die open en verlopen is (afgeleid, ADR 0018). */
  offerte: string | null;
}

export interface SignaalRijen {
  notities: { project_id: string; created_at: string }[];
  versies: { project_id: string; created_at: string }[];
  taken: { project_id: string; status: string | null; created_at: string; completed_at: string | null }[];
  offertes: { project_id: string; status: string; updated_at: string | null; valid_until?: string | null }[];
  mails: { matched_project_id: string; received_at: string | null }[];
  sites: { id: string; project_id: string | null }[];
  locaties: { project_id: string | null; site_id: string | null }[];
}

const later = (a: string | null, b: string | null | undefined) => (!b ? a : !a || b > a ? b : a);

export function bouwSignalen(r: SignaalRijen, vandaag: string = lokaleDag()): Map<string, Signalen> {
  const uit = new Map<string, Signalen>();
  const van = (id: string) => {
    let s = uit.get(id);
    if (!s) {
      s = { laatsteActiviteit: null, openTaken: 0, ems: false, offerte: null };
      uit.set(id, s);
    }
    return s;
  };
  for (const n of r.notities) van(n.project_id).laatsteActiviteit = later(van(n.project_id).laatsteActiviteit, n.created_at);
  for (const v of r.versies) van(v.project_id).laatsteActiviteit = later(van(v.project_id).laatsteActiviteit, v.created_at);
  for (const t of r.taken) {
    const s = van(t.project_id);
    s.laatsteActiviteit = later(later(s.laatsteActiviteit, t.created_at), t.completed_at);
    if (t.status !== 'completed') s.openTaken++;
  }
  const offerteTijd = new Map<string, string>();
  for (const q of r.offertes) {
    const s = van(q.project_id);
    s.laatsteActiviteit = later(s.laatsteActiviteit, q.updated_at);
    const t = q.updated_at ?? '';
    if (!offerteTijd.has(q.project_id) || t > offerteTijd.get(q.project_id)!) {
      offerteTijd.set(q.project_id, t);
      s.offerte = isVerlopen({ status: q.status, valid_until: q.valid_until ?? null }, vandaag) ? 'expired' : q.status;
    }
  }
  for (const m of r.mails) van(m.matched_project_id).laatsteActiviteit = later(van(m.matched_project_id).laatsteActiviteit, m.received_at);
  const projectVanSite = new Map(r.sites.filter((s) => s.project_id).map((s) => [s.id, s.project_id!]));
  for (const l of r.locaties) {
    const p = l.project_id ?? (l.site_id ? projectVanSite.get(l.site_id) : undefined);
    if (p) van(p).ems = true;
  }
  return uit;
}

const lijst = async <T>(p: PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> => {
  try {
    const r = await p;
    return r.error ? [] : r.data ?? [];
  } catch {
    return [];
  }
};

export async function haalSignalen(client: SignalenClient): Promise<Map<string, Signalen>> {
  const [notities, versies, taken, offertes, mails, sites, locaties] = await Promise.all([
    lijst<SignaalRijen['notities'][number]>(client.from('project_notes').select('project_id, created_at').order('created_at', { ascending: false }).limit(2000)),
    lijst<SignaalRijen['versies'][number]>(client.from('project_file_versions').select('project_id, created_at').order('created_at', { ascending: false }).limit(2000)),
    lijst<SignaalRijen['taken'][number]>(client.from('unified_todos').select('project_id, status, created_at, completed_at').not('project_id', 'is', null).limit(2000)),
    lijst<SignaalRijen['offertes'][number]>(client.from('quotes').select('project_id, status, updated_at, valid_until').not('project_id', 'is', null).is('deleted_at', null).limit(2000)),
    lijst<SignaalRijen['mails'][number]>(client.from('email_inbox').select('matched_project_id, received_at').not('matched_project_id', 'is', null).limit(2000)),
    lijst<SignaalRijen['sites'][number]>(client.from('sites').select('id, project_id').not('project_id', 'is', null)),
    lijst<SignaalRijen['locaties'][number]>(client.schema('ems').from('locatie').select('project_id, site_id').eq('actief', true)),
  ]);
  return bouwSignalen({ notities, versies, taken, offertes, mails, sites, locaties });
}
